/**
 * Audio-Analyse (Whisper/Diktat-Pipeline) → Analyse-Dokument auf Nextcloud + Editor-Öffnen.
 * Pattern analog Phase 1.4 Document-Ingest.
 */

import {
  PORTAL_DOCUMENT_UPLOAD_URL,
  PORTAL_PLAINTEXT_FETCH_URL,
  getPortalPbToken,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  resolveOnlyofficeIssueJwtUrl,
} from '../../constants';
import { createBlankDocxFile } from './blankDocx';
import {
  DIKTAT_OUTPUT_MIME,
  DIKTAT_PORTAL_FOLDER,
  DIKTAT_UPLOAD_WEBHOOK_PATH,
  diktatConvertOnlyDecision,
  diktatFileKind,
  diktatMaterializeGuard,
  diktatOutputFileName,
  diktatUploadHasSessionBearer,
  diktatUploadSessionHeaders,
  DIKTAT_UPLOAD_FAILED,
  diktatUploadIsTranscriptResult,
  diktatUsableTranscript,
  readDiktatUploadFields,
  parseDiktatOutputFormat,
  type DiktatOutputFormat,
} from './portalDiktatFlow';
import {
  fileExtensionFromName,
  isAudioFile,
  parsePortalDocumentN8nResponse,
  type PortalDocumentOpenFields,
} from './portalDocuments';
import { formatPortalFileSize, PORTAL_CHAT_ANALYZE_MAX_FILE_BYTES } from './portalChatDocumentAttach';

const DIKTAT_UPLOAD_URL = DIKTAT_UPLOAD_WEBHOOK_PATH;
const ANALYSIS_UPLOAD_FOLDER = DIKTAT_PORTAL_FOLDER;
const MAX_ANALYSIS_TEXT_CHARS = 120_000;

export interface PortalDiktatUploadResponse {
  ok?: boolean;
  error?: boolean;
  message?: string;
  pdfUrl?: string;
  pb_record_id?: string;
  pb_id?: string;
  filename?: string;
  nextcloud_path?: string;
  persist_nc?: boolean;
  persist_pb?: boolean;
  persist_share?: boolean;
  pb_warning?: string;
  protokollText?: string;
  transcriptText?: string;
  output_format?: string;
  output_mime?: string;
  analysis_pb_record_id?: string;
  analysis_document_url?: string;
  analysis_display_name?: string;
  analysis_nextcloud_path?: string;
}

export function validatePortalAudioAnalyzeFile(file: File): string | null {
  if (!isAudioFile(file)) return 'Keine Audiodatei erkannt.';
  if (file.size > PORTAL_CHAT_ANALYZE_MAX_FILE_BYTES) {
    return `Zu groß (max. ${formatPortalFileSize(PORTAL_CHAT_ANALYZE_MAX_FILE_BYTES)}).`;
  }
  if (file.size === 0) return 'Leere Datei.';
  return null;
}

function decodeUtf8Base64(b64: string): string {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return new TextDecoder('utf-8', { fatal: false }).decode(out);
}

/** PB/NC-Klartext nur wenn die Datei wirklich Transkript hat — kein Placeholder. */
export async function fetchStoredDiktatTranscript(args: {
  documentUrl?: string | null;
  nextcloudPath?: string | null;
  customerId: string;
  tenantExtras?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<string> {
  const url = String(args.documentUrl || '').trim();
  if (!url || !PORTAL_PLAINTEXT_FETCH_URL) return '';
  const proxyUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_PLAINTEXT_FETCH_URL);
  const body: Record<string, string> = {
    document_url: url,
    customerId: args.customerId,
    ...(args.tenantExtras || {}),
  };
  const path = String(args.nextcloudPath || '').trim();
  if (path) body.nextcloud_path = path;
  const res = await fetch(proxyUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...portalAuthzHeaders(proxyUrl),
    },
    credentials: 'omit',
    body: JSON.stringify(body),
    signal: args.signal,
  });
  let data: { ok?: boolean; text_utf8_base64?: string } = {};
  try {
    data = (await res.json()) as { ok?: boolean; text_utf8_base64?: string };
  } catch {
    return '';
  }
  if (!res.ok || !data.ok || typeof data.text_utf8_base64 !== 'string') return '';
  return diktatUsableTranscript(decodeUtf8Base64(data.text_utf8_base64));
}

function buildAnalysisFileName(sourceName: string): string {
  return diktatOutputFileName({ format: 'txt', sourceName });
}

export function buildAnalysisDocumentBody(
  sourceName: string,
  protokollText?: string,
  transcriptText?: string,
): string {
  const lines: string[] = [
    `Audio-Analyse: ${sourceName}`,
    `Erstellt: ${new Date().toLocaleString('de-AT')}`,
    '',
  ];
  if (protokollText?.trim()) {
    lines.push('=== Protokoll / Analyse ===', '', protokollText.trim(), '');
  }
  if (transcriptText?.trim() && transcriptText.trim() !== protokollText?.trim()) {
    lines.push('=== Transkript (Whisper) ===', '', transcriptText.trim(), '');
  }
  if (!protokollText?.trim() && !transcriptText?.trim()) {
    lines.push('Kein Analyse-Text vom Server erhalten.', '');
  }
  lines.push(
    '---',
    'KI-generiert — vor rechtsrelevanter Verwendung prüfen.',
  );
  return lines.join('\n').slice(0, MAX_ANALYSIS_TEXT_CHARS);
}

export async function submitPortalAudioForAnalysis(args: {
  file: File;
  customerId: string;
  kanzlei?: string;
  email?: string;
  signal?: AbortSignal;
}): Promise<PortalDiktatUploadResponse> {
  const { file, customerId, kanzlei, email, signal } = args;
  const err = validatePortalAudioAnalyzeFile(file);
  if (err) throw new Error(err);
  const sessionHeaders = diktatUploadSessionHeaders({
    token: getPortalPbToken(),
    listedAuthzHeaders: portalAuthzHeaders(DIKTAT_UPLOAD_URL),
  });
  if (!diktatUploadHasSessionBearer(sessionHeaders)) {
    throw new Error('Bitte neu anmelden (kein gültiges Portal-Token). Diktat-Upload startet nicht ohne Auth.');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('customerId', customerId);
  if (kanzlei?.trim()) formData.append('kanzlei', kanzlei.trim());
  if (email?.trim()) formData.append('email', email.trim());

  const res = await fetch(DIKTAT_UPLOAD_URL, {
    method: 'POST',
    body: formData,
    credentials: 'omit',
    headers: {
      ...sessionHeaders,
    },
    signal: signal ?? AbortSignal.timeout(300_000),
  });

  let data: PortalDiktatUploadResponse = {};
  try {
    data = (await res.json()) as PortalDiktatUploadResponse;
  } catch {
    /* leer */
  }

  if (!res.ok || data.error || data.ok === false || !diktatUploadIsTranscriptResult(data)) {
    throw new Error(
      data.message?.trim() ||
        (!res.ok
          ? `Audio-Analyse fehlgeschlagen (${res.status}).`
          : DIKTAT_UPLOAD_FAILED),
    );
  }

  const fields = readDiktatUploadFields(data);
  return {
    ...data,
    transcriptText: fields.transcriptText || data.transcriptText,
    protokollText: fields.protokollText || data.protokollText,
  };
}

export async function uploadPortalAnalysisTextDocument(args: {
  fileName: string;
  content: string;
  customerId: string;
  tenantExtras?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<PortalDocumentOpenFields> {
  const { fileName, content, customerId, tenantExtras = {}, signal } = args;
  const file = new File([content], fileName, { type: DIKTAT_OUTPUT_MIME.txt });
  return uploadPortalDiktatOutputFile({ file, customerId, tenantExtras, signal });
}

export async function uploadPortalDiktatOutputFile(args: {
  file: File;
  customerId: string;
  tenantExtras?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<PortalDocumentOpenFields> {
  const { file, customerId, tenantExtras = {}, signal } = args;
  if (!hasPortalAuthzToken()) {
    throw new Error('Upload: Bitte neu anmelden (kein gültiges Portal-Token).');
  }

  const fd = new FormData();
  fd.append('file', file);
  fd.set('customerId', customerId);
  fd.set('folderPath', ANALYSIS_UPLOAD_FOLDER);
  if (tenantExtras.tenant) fd.set('tenant', tenantExtras.tenant);

  const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_UPLOAD_URL);
  const res = await fetch(url, {
    method: 'POST',
    body: fd,
    credentials: 'omit',
    headers: {
      ...portalAuthzHeaders(url),
    },
    signal,
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    /* leer */
  }
  const fields = parsePortalDocumentN8nResponse(data, res);
  if (!fields.document_url) {
    throw new Error('Konvertierung fehlgeschlagen — keine Datei gespeichert.');
  }
  return fields;
}

function fieldsFromDiktatPdf(diktat: PortalDiktatUploadResponse): PortalDocumentOpenFields | null {
  const url = diktat.pdfUrl?.trim();
  const path = diktat.nextcloud_path?.trim();
  const name = diktat.filename?.trim() || '';
  if (!url) return null;
  const isPdf =
    diktat.output_format === 'pdf' || diktatFileKind(name, path) === 'pdf';
  if (!isPdf) return null;
  return {
    display_name: name || 'Protokoll.pdf',
    document_url: url,
    pb_record_id: diktat.pb_record_id?.trim() || diktat.pb_id?.trim(),
    nextcloud_path: path,
  };
}

async function convertDiktatSourceToPdf(args: {
  sourceText: string;
  customerId: string;
  kanzlei?: string;
  email?: string;
  signal?: AbortSignal;
}): Promise<PortalDocumentOpenFields> {
  const decision = diktatConvertOnlyDecision({
    hasAudioFile: false,
    outputFormat: 'pdf',
    sourceText: args.sourceText,
  });
  if (!decision.ok) throw new Error(decision.message);

  const sessionHeaders = diktatUploadSessionHeaders({
    token: getPortalPbToken(),
    listedAuthzHeaders: portalAuthzHeaders(DIKTAT_UPLOAD_URL),
  });
  if (!diktatUploadHasSessionBearer(sessionHeaders)) {
    throw new Error('Bitte neu anmelden (kein gültiges Portal-Token). PDF wird nicht erzeugt.');
  }

  const formData = new FormData();
  formData.append('customerId', args.customerId);
  formData.append('outputFormat', 'pdf');
  formData.append('convertOnly', 'true');
  formData.append('sourceText', diktatUsableTranscript(args.sourceText) || args.sourceText);
  if (args.kanzlei?.trim()) formData.append('kanzlei', args.kanzlei.trim());
  if (args.email?.trim()) formData.append('email', args.email.trim());

  const res = await fetch(DIKTAT_UPLOAD_URL, {
    method: 'POST',
    body: formData,
    credentials: 'omit',
    headers: { ...sessionHeaders },
    signal: args.signal ?? AbortSignal.timeout(120_000),
  });
  let data: PortalDiktatUploadResponse = {};
  try {
    data = (await res.json()) as PortalDiktatUploadResponse;
  } catch {
    /* leer */
  }
  if (!res.ok || data.error || data.ok === false) {
    throw new Error(
      data.message?.trim() ||
        `PDF-Konvertierung fehlgeschlagen (DIKTAT_UPLOAD HTTP ${res.status}).`,
    );
  }
  const fields = fieldsFromDiktatPdf(data);
  if (!fields) {
    throw new Error('PDF-Konvertierung fehlgeschlagen — keine Datei erzeugt.');
  }
  return fields;
}

/**
 * Klasse DIKTAT_OUTPUT_FORMAT — Datei im Ordner erzeugen, fail-closed ohne Fake.
 */
export async function materializeDiktatOutputFormat(args: {
  format: DiktatOutputFormat;
  sourceName: string;
  bodyText: string;
  customerId: string;
  kanzlei?: string;
  email?: string;
  tenantExtras?: Record<string, string>;
  existingPdf?: PortalDocumentOpenFields | null;
  signal?: AbortSignal;
}): Promise<{ format: DiktatOutputFormat; fields: PortalDocumentOpenFields }> {
  const format = parseDiktatOutputFormat(args.format);
  if (!format) {
    throw new Error('Unbekanntes Format — Datei wird nicht erzeugt.');
  }
  const transcript = diktatUsableTranscript(args.bodyText);
  const guard = diktatMaterializeGuard({
    format,
    bodyText: transcript || args.bodyText,
    existingPdfUrl: args.existingPdf?.document_url,
  });
  if (!guard.ok) throw new Error(guard.message);

  if (format === 'pdf') {
    const existing = args.existingPdf?.document_url ? args.existingPdf : null;
    if (existing) return { format, fields: existing };
    const fields = await convertDiktatSourceToPdf({
      sourceText: transcript,
      customerId: args.customerId,
      kanzlei: args.kanzlei,
      email: args.email,
      signal: args.signal,
    });
    return { format, fields };
  }

  if (format === 'txt') {
    const fields = await uploadPortalAnalysisTextDocument({
      fileName: diktatOutputFileName({ format: 'txt', sourceName: args.sourceName }),
      content: args.bodyText,
      customerId: args.customerId,
      tenantExtras: args.tenantExtras,
      signal: args.signal,
    });
    return { format, fields };
  }

  const file = createBlankDocxFile(
    diktatOutputFileName({ format: 'docx', sourceName: args.sourceName }),
    {
      kanzleiName: args.kanzlei,
      bodyText: args.bodyText,
    },
  );
  if (!file.size) {
    throw new Error('DOCX-Erzeugung fehlgeschlagen — keine Datei.');
  }
  const fields = await uploadPortalDiktatOutputFile({
    file,
    customerId: args.customerId,
    tenantExtras: args.tenantExtras,
    signal: args.signal,
  });
  return { format, fields };
}

export function buildAnalysisFieldsFromDiktatResponse(
  sourceName: string,
  diktat: PortalDiktatUploadResponse,
  uploaded?: PortalDocumentOpenFields,
): PortalDocumentOpenFields | null {
  if (uploaded?.document_url) return uploaded;

  const analysisUrl = diktat.analysis_document_url?.trim();
  if (!analysisUrl) return null;

  return {
    display_name:
      diktat.analysis_display_name?.trim() ||
      buildAnalysisFileName(sourceName),
    document_url: analysisUrl,
    pb_record_id:
      diktat.analysis_pb_record_id?.trim() ||
      diktat.pb_record_id?.trim() ||
      diktat.pb_id?.trim(),
    nextcloud_path: diktat.analysis_nextcloud_path?.trim(),
  };
}

/**
 * Audio → Diktat-Webhook → Analyse-Text als Portal-Dokument.
 */
export async function prepareAudioAnalyzeDocumentContext(args: {
  file: File;
  customerId: string;
  kanzlei?: string;
  email?: string;
  tenantExtras?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<{
  documentContext: string;
  fileNames: string[];
  uploadedFields: PortalDocumentOpenFields[];
  diktatResponse: PortalDiktatUploadResponse;
}> {
  const { file, customerId, kanzlei, email, signal } = args;

  const diktat = await submitPortalAudioForAnalysis({
    file,
    customerId,
    kanzlei,
    email,
    signal,
  });

  const analysisText = buildAnalysisDocumentBody(
    file.name,
    diktat.protokollText,
    diktat.transcriptText,
  );

  // Klasse DIKTAT_OUTPUT_FORMAT: kein automatisches Analyse_*.txt — Format wählt der User.
  const fields =
    fieldsFromDiktatPdf(diktat) ||
    buildAnalysisFieldsFromDiktatResponse(file.name, diktat);

  const uploadedFields = fields ? [fields] : [];
  const ext = fields ? fileExtensionFromName(fields.display_name) : 'txt';

  const documentContext = [
    '=== Audio-Analyse ===',
    '',
    `Quelldatei: ${file.name}`,
    `Typ: ${ext}`,
    '',
    'Auszug:',
    analysisText.slice(0, 7500),
  ].join('\n');

  return {
    documentContext,
    fileNames: [file.name],
    uploadedFields,
    diktatResponse: diktat,
  };
}
